/* ── Public Player Profile ────────────────────────────────────────────────
   Unauthenticated route: /players/:profileId
   Rich career view (stats, badges, stock history) pulled from public views.
   No private data, no photos gallery.
──────────────────────────────────────────────────────────────────────── */
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Trophy,
  ShieldAlert,
  TrendingUp,
  TrendingDown,
  Minus,
  Award,
  Zap,
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { supabase } from "../supabaseClient";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";
import CroppedAvatar from "../components/CroppedAvatar";
import {
  groupBadgeDefs,
  BadgeCard,
  FamilyBadgeCard,
} from "../components/BadgeDisplay";
import CareerHighsSection, {
  fetchCareerHighs,
} from "../components/CareerHighsSection";
import PlayerCareerSection from "../components/PlayerCareerSection";

const PublicPlayerProfile = () => {
  const { profileId } = useParams();
  const [profile, setProfile] = useState(null);
  const [careerStats, setCareerStats] = useState(null);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [unlockedBadgeKeys, setUnlockedBadgeKeys] = useState(new Set());
  const [stockHistory, setStockHistory] = useState([]);
  const [careerHighs, setCareerHighs] = useState([]);
  const [ddGames, setDdGames] = useState([]);
  const [tdGames, setTdGames] = useState([]);
  const [careerHighsLoading, setCareerHighsLoading] = useState(false);
  const [linkedSeasons, setLinkedSeasons] = useState([]);
  const [seasonStats, setSeasonStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setNotFound(false);

      const [
        { data: profileRow },
        { data: statsRow },
        { data: badgeDefsData, error: badgeDefsErr },
        { data: unlockedRows, error: unlockedErr },
        { data: historyRows },
        { data: linkedPlayerRows },
      ] = await Promise.all([
        supabase
          .from("public_player_profiles")
          .select("*")
          .eq("profile_id", profileId)
          .maybeSingle(),
        supabase
          .from("public_player_career_stats")
          .select("*")
          .eq("profile_id", profileId)
          .maybeSingle(),
        supabase
          .from("badges")
          .select("*")
          .order("threshold", { ascending: true }),
        supabase
          .from("public_player_badges")
          .select("key")
          .eq("profile_id", profileId),
        supabase
          .from("public_player_stock_history")
          .select("*")
          .eq("profile_id", profileId)
          .order("game_date", { ascending: true }),
        // Fetch all player rows linked to this profile (for career highs + per-season stats)
        supabase
          .from("players")
          .select(
            "id, season_id, team_id, seasons(id, name), teams(name, primary_color, secondary_color, gradient)",
          )
          .eq("profile_id", profileId)
          .not("team_id", "is", null)
          .order("season_id", { ascending: false }),
      ]);

      if (cancelled) return;

      if (badgeDefsErr) console.error("badges fetch failed:", badgeDefsErr);
      if (unlockedErr)
        console.error("public_player_badges fetch failed:", unlockedErr);

      if (!profileRow) {
        setNotFound(true);
      } else {
        setProfile(profileRow);
        setCareerStats(statsRow);
        setBadgeDefs(badgeDefsData || []);
        setUnlockedBadgeKeys(new Set((unlockedRows || []).map((r) => r.key)));
        setStockHistory(historyRows || []);

        // Build per-season stats + career highs from linked player rows
        const linked = linkedPlayerRows || [];
        setLinkedSeasons(linked);

        const playerIds = linked.map((r) => r.id);
        if (playerIds.length > 0) {
          // Per-season stat aggregation
          const { data: statRows } = await supabase
            .from("game_stats")
            .select(
              "player_id, two_pm, three_pm, ft_made, rebounds, assists, steals, personal_fouls, technical_fouls, games!inner(counts_in_standings)",
            )
            .in("player_id", playerIds)
            .eq("games.counts_in_standings", true);

          const seasonByPlayerId = Object.fromEntries(
            linked.map((p) => [p.id, p]),
          );
          const grouped = {};
          (statRows || []).forEach((row) => {
            const pts =
              (row.two_pm ?? 0) * 2 +
              (row.three_pm ?? 0) * 3 +
              (row.ft_made ?? 0);
            const isDNP =
              pts === 0 &&
              (row.rebounds ?? 0) === 0 &&
              (row.assists ?? 0) === 0 &&
              (row.steals ?? 0) === 0 &&
              (row.three_pm ?? 0) === 0 &&
              (row.two_pm ?? 0) === 0 &&
              (row.ft_made ?? 0) === 0 &&
              (row.personal_fouls ?? 0) === 0 &&
              (row.technical_fouls ?? 0) === 0;
            if (isDNP) return;
            const player = seasonByPlayerId[row.player_id];
            if (!player) return;
            const key = player.season_id;
            if (!grouped[key]) {
              grouped[key] = {
                gp: 0,
                pts: 0,
                reb: 0,
                ast: 0,
                stl: 0,
                three_pm: 0,
              };
            }
            const s = grouped[key];
            s.gp += 1;
            s.pts += pts;
            s.reb += row.rebounds || 0;
            s.ast += row.assists || 0;
            s.stl += row.steals || 0;
            s.three_pm += row.three_pm || 0;
          });
          if (!cancelled) setSeasonStats(grouped);

          // Career highs
          setCareerHighsLoading(true);
          const {
            highs,
            ddGames: dd,
            tdGames: td,
          } = await fetchCareerHighs(supabase, playerIds);
          if (!cancelled) {
            setCareerHighs(highs);
            setDdGames(dd);
            setTdGames(td);
            setCareerHighsLoading(false);
          }
        }
      }
      setLoading(false);
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-3xl animate-pulse mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-80 bg-gray-100 dark:bg-gray-800 rounded-3xl animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <ShieldAlert
          size={48}
          className="mx-auto mb-6 text-gray-300 dark:text-gray-700"
        />
        <h1 className="text-3xl font-bold dark:text-white mb-3">
          Player not found
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-8">
          This profile doesn't exist or has no public career data yet.
        </p>
        <Link
          to="/"
          className="text-orange-600 dark:text-orange-400 font-medium hover:underline inline-flex items-center gap-2"
        >
          <ArrowLeft size={18} /> Back to Home
        </Link>
      </div>
    );
  }

  const teamStyle = getTeamStyle({
    name: profile.team_name,
    primary_color: profile.primary_color,
    secondary_color: profile.secondary_color,
    gradient: profile.gradient,
  });
  const teamTextColor = getTextColorForAccent(
    teamStyle.primaryHex,
    isDarkModeActive(),
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Hero Header */}
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 p-8 mb-10">
        <div className="flex flex-col md:flex-row items-start gap-8">
          <CroppedAvatar
            avatarUrl={profile.avatar_url}
            avatarCrop={profile.avatar_crop}
            size={160}
            className="border-4 border-white dark:border-gray-900 shadow-lg"
          />
          <div className="flex-1 pt-2">
            <h1 className="text-4xl font-bold tracking-tight dark:text-white mb-2">
              {profile.name}
            </h1>
            <div className="flex items-center gap-3 flex-wrap mb-6">
              {profile.team_name && (
                <span
                  className="inline-flex items-center rounded-full font-semibold px-4 py-1.5 text-lg"
                  style={{ ...teamStyle.headerStyle, color: teamTextColor }}
                >
                  {profile.team_name}
                </span>
              )}
              {profile.jersey_number != null && (
                <span className="text-2xl font-mono text-gray-400 dark:text-gray-500">
                  #{profile.jersey_number}
                </span>
              )}
              {profile.season_name && (
                <span className="text-xl text-gray-500 dark:text-gray-400">
                  {profile.season_name}
                </span>
              )}
            </div>

            {/* Quick Stats Row */}
            {careerStats && (
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400">
                    {careerStats.games_played || 0}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Career Games
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400">
                    {careerStats.avg_points?.toFixed(1) ?? "0.0"}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Career PPG
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400">
                    {careerStats.points ?? 0}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Career Points
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400">
                    {careerStats.avg_rebounds?.toFixed(1) ?? "0.0"}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Career RPG
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400">
                    {careerStats.rebounds ?? 0}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Career Rebounds
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400">
                    {careerStats.avg_assists?.toFixed(1) ?? "0.0"}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Career APG
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-orange-600 dark:text-orange-400">
                    {careerStats.assists ?? 0}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Career Assists
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Player Career — per-season breakdown */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500/10">
            <Trophy size={22} className="text-orange-500" />
          </div>
          <h2 className="text-3xl font-semibold tracking-tight dark:text-white">
            Player Career
          </h2>
        </div>
        <PlayerCareerSection
          seasons={linkedSeasons}
          seasonStats={seasonStats}
          loading={loading}
        />
      </div>

      {/* Career Highs */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500/10">
            <Zap size={22} className="text-orange-500" />
          </div>
          <h2 className="text-3xl font-semibold tracking-tight dark:text-white">
            Career Highs
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          All-time single-game peak performances across every season.
        </p>
        <CareerHighsSection
          careerHighs={careerHighs}
          ddGames={ddGames}
          tdGames={tdGames}
          loading={careerHighsLoading}
        />
      </div>

      {/* Badges / Achievements */}
      {badgeDefs.length > 0 && careerStats && (
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500/10">
              <Award size={22} className="text-orange-500" />
            </div>
            <h2 className="text-3xl font-semibold tracking-tight dark:text-white">
              Achievements
            </h2>
          </div>
          {(() => {
            const { families, standalone } = groupBadgeDefs(badgeDefs);
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {standalone.map((badge) => (
                  <BadgeCard
                    key={badge.key}
                    badge={badge}
                    unlocked={unlockedBadgeKeys.has(badge.key)}
                    currentValue={careerStats?.[badge.stat_key] ?? 0}
                  />
                ))}
                {Object.entries(families).map(([familyKey, tiers]) => (
                  <FamilyBadgeCard
                    key={familyKey}
                    tiers={tiers}
                    unlockedKeys={unlockedBadgeKeys}
                    currentValue={careerStats?.[tiers[0].stat_key] ?? 0}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Player Stock History */}
      {stockHistory.length >= 2 && (
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-500/10">
              <TrendingUp size={22} className="text-orange-500" />
            </div>
            <h2 className="text-3xl font-semibold tracking-tight dark:text-white">
              Player Stock
            </h2>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 border border-gray-100 dark:border-gray-800">
            {(() => {
              const latest = stockHistory[stockHistory.length - 1];
              const prev = stockHistory[stockHistory.length - 2];
              const delta =
                Number(latest.stock_value) - Number(prev.stock_value);
              const trendUp = delta > 0.05;
              const trendDown = delta < -0.05;

              return (
                <>
                  <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 mb-1">
                        CURRENT STOCK VALUE
                      </p>
                      <p className="text-4xl font-bold tracking-tight dark:text-white">
                        {Number(latest.stock_value).toFixed(1)}
                      </p>
                    </div>
                    <div
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
                        trendUp
                          ? "bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400"
                          : trendDown
                            ? "bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400"
                            : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      }`}
                    >
                      {trendUp ? (
                        <TrendingUp size={16} />
                      ) : trendDown ? (
                        <TrendingDown size={16} />
                      ) : (
                        <Minus size={16} />
                      )}
                      {delta > 0 ? "+" : ""}
                      {delta.toFixed(1)} last game
                    </div>
                  </div>

                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={stockHistory}>
                      <XAxis dataKey="game_date" hide />
                      <ReferenceLine
                        y={100}
                        stroke="#d1d5db"
                        strokeDasharray="4 4"
                      />
                      <Tooltip
                        formatter={(value) => [
                          Number(value).toFixed(1),
                          "Stock",
                        ]}
                        labelFormatter={(label) =>
                          new Date(label).toLocaleDateString()
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey="stock_value"
                        stroke="#f97316"
                        strokeWidth={3}
                        dot={false}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
                    Starts at 100. Moves based on performance vs career average.
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      )}

      <div className="mt-12 text-center">
        <Link
          to="/"
          className="text-orange-600 dark:text-orange-400 hover:underline inline-flex items-center gap-2"
        >
          <ArrowLeft size={18} /> Back to League Home
        </Link>
      </div>
    </div>
  );
};

export default PublicPlayerProfile;
